import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export type PDFOptimizeMode = 'screen' | 'print' | 'prepress';

export const useQuotePDF = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generate PDF and return bytes (for preview/embedding)
  const generatePDFBytes = async (
    quoteId: string,
    includeTerms: boolean = true,
    optimize: PDFOptimizeMode = 'screen'
  ): Promise<Uint8Array | null> => {
    setIsGenerating(true);
    setError(null);
    
    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        'generate-quote-pdf',
        {
          body: { quoteId, includeTerms, optimize }
        }
      );

      if (invokeError) {
        console.error('Edge function error:', invokeError);
        throw new Error(invokeError.message || 'Failed to generate PDF');
      }

      if (!data) {
        throw new Error('No PDF data received');
      }

      // Convert base64 to bytes
      const binaryString = atob(data.pdf);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      return bytes;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('PDF generation error:', errorMessage);
      setError(errorMessage);
      return null;
    } finally {
      setIsGenerating(false);
    }
  };

  const generatePDF = async (
    quoteId: string,
    includeTerms: boolean = true,
    download: boolean = true,
    optimize: PDFOptimizeMode = 'screen'
  ): Promise<boolean> => {
    setIsGenerating(true);
    setError(null);
    
    try {
      console.log('Generating PDF for quote:', quoteId, { optimize });
      
      const { data, error: invokeError } = await supabase.functions.invoke(
        'generate-quote-pdf',
        {
          body: { quoteId, includeTerms, optimize }
        }
      );

      if (invokeError) {
        console.error('Edge function error:', invokeError);
        throw new Error(invokeError.message || 'Failed to generate PDF');
      }

      if (!data) {
        throw new Error('No PDF data received');
      }

      // Convert base64 to blob
      const binaryString = atob(data.pdf);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      if (download) {
        // Download PDF
        const link = document.createElement('a');
        link.href = url;
        link.download = `${data.filename || 'offerte'}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        const qualityLabel = optimize === 'prepress' ? 'prepress' : optimize === 'print' ? 'print' : 'screen';
        toast.success(`PDF gedownload (${qualityLabel} kwaliteit)`);
        
        // Show warning if there were unresolved placeholders
        if (data.warnings) {
          toast.warning('Let op: controleer de offerte', {
            description: data.warnings.message,
            duration: 6000,
          });
        }
      } else {
        // Open in new tab
        window.open(url, '_blank');
        toast.success('PDF geopend in nieuwe tab');
      }

      // Cleanup
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
      
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('PDF generation error:', errorMessage);
      setError(errorMessage);
      toast.error(`PDF genereren mislukt: ${errorMessage}`);
      return false;
    } finally {
      setIsGenerating(false);
    }
  };

  const previewPDF = async (quoteId: string, includeTerms: boolean = true) => {
    return generatePDF(quoteId, includeTerms, false);
  };

  const exportPDF = async (quoteId: string, optimize: PDFOptimizeMode = 'screen') => {
    return generatePDF(quoteId, true, true, optimize);
  };

  return { generatePDF, generatePDFBytes, previewPDF, exportPDF, isGenerating, error };
};
